// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { IGroupSettings } from '../group-settings';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { IEndpoint } from '../../../../types/subscription';
import { D3eService } from '../../../../services/d3e-service';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { IPolicyListItem } from '../../../../types/d3ePolicy';
import { LicenseMethods } from '../../../../shared/license.methods';
import { UniqueGroupNameValidator } from '../../../shared/validators/custom-validators';
import { TranslateService } from '@ngx-translate/core';
import { IEndpointsResponse } from '../../../../types/endpoints';
import { ALL_ORGS_ID } from '../../../../types/group';
import { PageEvent } from '@angular/material/paginator';
import { withLoading } from '../../../../shared/misc';
import { ActivatedRoute } from '@angular/router';
import dayjs from 'dayjs';


interface ISortableDevice extends IEndpoint {
  id: number;
  selected: boolean;
  checkboxTooltip: string;
}

@Component({
    selector: 'app-group-details-device',
    templateUrl: './group-details-device.component.html',
    styleUrls: ['./group-details-device.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GroupDetailsDeviceComponent implements OnChanges {
  search = '';
  selectedIds: Map<number, boolean> = new Map<number, boolean>();
  loadingDevices$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  loadingPolicies$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  @Input() groupSettings: IGroupSettings;
  @Input() groupNameValidator: UniqueGroupNameValidator;
  @Input() selectedOrgId: string;
  @Output() groupSettingsChange: EventEmitter<IGroupSettings> = new EventEmitter<IGroupSettings>();

  sortAttrs = [{'sortAttr': 'createdDate', 'sortOrder': 'desc'}];
  form: UntypedFormGroup;
  dataSource: MatTableDataSource<ISortableDevice> = new MatTableDataSource<ISortableDevice>();
  columns = ['select', 'name', 'group', 'policy', 'os', 'createdDate', 'last_logged_in_user', 'lastSeen'];
  trackById = (_: number, device: ISortableDevice) => device.id;
  selection: SelectionModel<ISortableDevice> = new SelectionModel<ISortableDevice>(true);
  policies: IPolicyListItem[];
  defaultPageSize = 10;
  maxPageSize = 1000;
  pageSize = 10;
  pageSizeOptions: number[] = [5, 10, 25, 50, 100, 500, 1000];
  length = 0;
  pageIndex = 0;
  loadingSkeletonRows: {}[];
  formSkeletonTheme = {
    height: '40px',
    width: '25em',
    margin: '10px',
    padding: '0'
  };

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly d3eSvc: D3eService,
    private readonly route: ActivatedRoute,
    public readonly translate: TranslateService,
  ) {
    this.form = fb.group({
      name: ['', Validators.required],
      description: [''],
      policy: ['', Validators.required],
    });
    let savedPageSize = parseInt((localStorage.getItem("gdp_size") || this.defaultPageSize.toString()), 10);
    savedPageSize = savedPageSize > this.maxPageSize ? this.defaultPageSize : savedPageSize;
    this.pageSize = savedPageSize;
    localStorage.setItem("gdp_size", savedPageSize.toString());
    this.loadingSkeletonRows = Array(10).fill({});
  }

  ngOnChanges(changes: SimpleChanges) {
    const settingsFirstChange = changes['groupSettings']?.firstChange;
    const settingsHasChanged = changes['groupSettings']?.currentValue !== changes['groupSettings']?.previousValue;
    if (settingsFirstChange && settingsHasChanged) {
      if (this.isDefaultGroup()) {
        this.form.get('name').disable();
        this.form.get('description').disable();
        this.form.get('policy').disable();
      }
      let observables: [Observable<IEndpointsResponse>, Observable<IPolicyListItem[]>];
      observables = [
        this.searchDevices(this.selectedOrgId, this.pageSize, 0),
        this.getPolicies(this.selectedOrgId)
      ];
      forkJoin(observables).subscribe({
        next: ([devices, policies]: [IEndpointsResponse, IPolicyListItem[]]) => {
          this.selectedIds.clear();
          this.selection.clear();
          changes.groupSettings.currentValue.members.forEach(deviceId => this.selectedIds.set(deviceId, true));
          this.setPolicies(policies);
          this.setDevices(devices);
          this.length = devices.total;

          this.form.setValue({
            name: changes.groupSettings.currentValue.name,
            description: changes.groupSettings.currentValue.description,
            policy: changes.groupSettings.currentValue.policyId || -1,
          });
        },
        error: error => {
          console.error(error);
        }
      });
    }

    if (changes?.groupNameValidator && changes?.groupNameValidator?.currentValue) {
      this.form.controls.name.setAsyncValidators([changes.groupNameValidator.currentValue?.validate.bind(this.groupNameValidator)]);
    }
  }

  searchDevices(selectedOrgId: string, pageSize: number, pageIndex: number): Observable<IEndpointsResponse> {

    const query = {
      search: this.search,
      include_group_info: true,
      checkCompliance: false,
      selectedOrgId: selectedOrgId == ALL_ORGS_ID ? undefined : selectedOrgId,
      pageSize: pageSize,
      pageStart: pageIndex * pageSize
    };

      if (this.sortAttrs.length) {
        const sorts = [];
        for (let i = 0; i < this.sortAttrs.length; i++) {
            const sort = {
                'attr': this.sortAttrs[i]['sortAttr'],
                'desc': (this.sortAttrs[i]['sortOrder'] === 'desc')
            };
            switch (sort['attr']) {
                case 'name':
                    sort['attr'] = 'computer_name';
                    break;
                case 'os':
                    sort['attr'] = 'os_version';
                    break;
                case 'createdDate':
                    sort['attr'] = 'created_dttm';
                    break;
                case 'lastLoggedIn':
                    sort['attr'] = 'last_logged_in_user';
                    break;
                case 'lastSeen':
                    sort['attr'] = 'last_check_dttm';
                    break;
                default:
                    continue;
            }
            sorts.push(sort);
        }
        if (sorts.length) {
            query['sorts'] = sorts;
        }
    }

    return this.d3eSvc.get_home_devices(query).pipe(
        withLoading(isLoading => this.loadingDevices$.next(isLoading))
    );
  }

  getDevices() {
      this.searchDevices(this.selectedOrgId, this.pageSize, this.pageIndex).subscribe(this.setDevices.bind(this));
  }

  isDefaultGroup(): boolean {
      return this.route.snapshot.paramMap.get('type') === 'device' && this.route.snapshot.paramMap.get('id') === '-1';
  }

  setDevices(response: IEndpointsResponse) {
    this.selection.clear();
    const devices = response.devices.map((d: ISortableDevice) => {
      LicenseMethods.setLastSeen(d, dayjs().utc());
      d.os = LicenseMethods.getOsVersion(d);
      d.selected = false;
      d.checkboxTooltip = ((<IEndpoint>d).device_set_id > -1) ? this.translate.instant(
        'groups.device-already-assigned', {group: d.device_set_name}) : '';
      return d;
    });
    for (const device of devices as ISortableDevice[]) {
      if (!this.selectedIds.has(device.id)) {
        this.selectedIds.set(device.id, false);
        device.selected = false;
      }
      if (this.selectedIds.get(device.id)) {
        this.selection.select(device);
        device.selected = true;
      } else {
        device.selected = false;
      }
    }

    this.length = response.total;
    this.dataSource.data = devices;
  }

  getPolicies(selectedOrgId: string): Observable<IPolicyListItem[]> {
    return this.d3eSvc.getD3EPolicies(selectedOrgId == ALL_ORGS_ID ? undefined : selectedOrgId).pipe(
        withLoading(isLoading => this.loadingPolicies$.next(isLoading)),
    );
  }

  setPolicies(policies: IPolicyListItem[]) {
    let lowestPriorityPolicy: IPolicyListItem = null;
    this.policies = policies;
    const value = this.form.controls['policy'].value
    if ( value == null || value == '' ) {
      // set the policy to the default policy
      this.policies.forEach(policy => {
        if (lowestPriorityPolicy == null) {
          lowestPriorityPolicy = policy;
        } else {
          if (policy.priority < lowestPriorityPolicy.priority) {
            lowestPriorityPolicy = policy;
          }
        }
      });
      if (lowestPriorityPolicy != null && (value == '' || value == null)) {
        this.form.controls['policy'].setValue(lowestPriorityPolicy.id);
      }
    }
  }

  emitChanges() {
    const members: number[] = [];
    for (const licenseId of this.selectedIds.keys()) {
      if (this.selectedIds.get(licenseId)) {
        members.push(licenseId);
      }
    }
    const settings: IGroupSettings = {
      groupId: this.groupSettings.groupId,
      name: this.form.value.name,
      description: this.form.value.description,
      policyId: this.form.value.policy,
      members: members,
    };
    this.groupSettingsChange.emit(settings);
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data
        .filter((d: ISortableDevice) =>
          d.device_set_id === this.groupSettings.groupId || d.device_set_id === -1
        ).length;
    // This is >= and not === to allow the deselection of an endpoint that is in a disabled row.
    // Meaning it is also in another group, which we do not want.
    return numSelected >= numRows;
  }

  isRowDisabled(device: ISortableDevice) {
    return (!this.selection.isSelected(device) && device.device_set_id !== this.groupSettings.groupId && device.device_set_id !== -1) || this.isDefaultGroup();
  }

  isAllDisabled() {
      const data = this.dataSource.data;
      return data.length > 0 && data.every(d => this.isRowDisabled(d));
  }

  masterToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
      this.dataSource.data.forEach((device: ISortableDevice) => {
        this.selectedIds.set(device.id, false);
        device.selected = false;
      });
    } else {
      this.dataSource.data.forEach((device: ISortableDevice) => {
        // We don't want to select endpoints in other groups, but if we find an endpoint that is selected
        // and in another group we deselect the endpoint from this group (in the else) even though
        // we are doing a select all.
        if (device.device_set_id == this.groupSettings.groupId || device.device_set_id == -1) {
          this.selection.select(device);
          this.selectedIds.set(device.id, true);
          device.selected = true;
        } else {
          this.selectedIds.set(device.id, false);
          device.selected = false;
        }
      });
    }
  }

  toggleAll() {
      this.masterToggle();
      this.emitChanges();
  }

  toggleRow(toggle: boolean, row: ISortableDevice) {
    if (toggle && row.device_set_id !== this.groupSettings.groupId && row.device_set_id !== -1) {
      return;
    }
    if (toggle) {
      this.selection.select(row);
    } else {
      this.selection.deselect(row);
    }
    this.selectedIds.set(row.id, toggle);
    row.selected = toggle;
    this.emitChanges();
  }

  sortColumn(event) {
    const sort = {
        'sortAttr': event.active,
        'sortOrder': event.direction
    };

    this.sortAttrs = [sort];
    this.pageIndex = 0;
    this.getDevices();
  }

  handlePageEvent(e: PageEvent) {
    this.length = e.length;
    this.pageSize = e.pageSize;
    this.pageIndex = e.pageIndex;
    localStorage.setItem('gdp_size', this.pageSize.toString());
    this.getDevices();
  }

  getGroupTooltip(device: ISortableDevice): string {
    return `Endpoint already assigned to the "${device.device_set_name}" group`;
  }

}
